import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Phone, 
  MessageCircle, 
  Calendar, 
  MapPin, 
  ChevronLeft, 
  ChevronRight, 
  X,
  Gauge,
  Hash,
  Settings,
  Car,
  Palette,
  Droplets,
  Zap
} from 'lucide-react';

const DetailsContainer = styled.div`
  min-height: 100vh;
  background: #0f0f0f;
  color: #ffffff;
`;

const DetailsHeader = styled.section`
  background: #1a1a1a;
  padding: 15px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    padding: 15px 0;
  }
`;

const HeaderContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const HeaderTitle = styled.div`
  h3 {
    font-size: 1.8rem;
    font-weight: 600;
    color: #ffffff;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  p {
    font-size: 1.1rem;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const HeaderButtons = styled.div`
  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    
    @media (max-width: 768px) {
      justify-content: flex-start;
      flex-wrap: wrap;
    }
  }
  
  li {
    display: flex;
  }
`;

const HeaderButton = styled.button`
  background: ${props => props.primary ? 'linear-gradient(45deg, #F43F60, #FF6B6B)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  padding: 8px 15px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
`;

const MainContent = styled.main`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const VdpArea = styled.section`
  margin-bottom: 3rem;
`;

const DetailsSlider = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const SliderContainer = styled.div`
  position: relative;
  height: 500px;
  
  @media (max-width: 768px) {
    height: 300px;
  }
`;

const SliderImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const SliderNav = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0 1rem;
  pointer-events: none;
`;

const SliderButton = styled.button`
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  pointer-events: all;
  
  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
`;

const ThumbnailGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 0.5rem;
  margin-top: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  }
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  
  &:hover {
    transform: scale(1.01);
    border-color: #F43F60;
  }
  
  &.active {
    border-color: #F43F60;
    box-shadow: 0 0 0 2px rgba(244, 63, 96, 0.3);
  }
`;

const DetailsContent = styled.section`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 3rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const CarTitle = styled.div`
  p {
    color: #F43F60;
    font-weight: 500;
    margin: 0 0 0.5rem 0;
    text-transform: capitalize;
    font-size: 16px;
  }
  
  h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
    color: #ffffff;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  .vin {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    margin: 0 0 1rem 0;
  }
  
  .description {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
    margin: 0;
  }
`;

const AboutCarsArea = styled.div`
  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0 0 1.5rem 0;
    color: #F43F60;
  }
`;

const AboutCarsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 1rem;
  }
`;

const AboutCar = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const CarIcon = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(244, 63, 96, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #F43F60;
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const CarInfo = styled.div`
  h4 {
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    margin: 0 0 0.25rem 0;
    text-transform: uppercase;
  }
  
  p {
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    margin: 0;
    text-transform: capitalize;
  }
`;

const DetailsCon = styled.div`
  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0 0 1rem 0;
    color: #F43F60;
  }
  
  p {
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.6;
    margin: 0 0 1rem 0;
  }
`;

const ActionBoxes = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ActionBox = styled.div`
  background: ${props => props.primary ? 'linear-gradient(135deg, rgba(244, 63, 96, 0.1) 0%, rgba(255, 107, 107, 0.1) 100%)' : 'rgba(255, 255, 255, 0.05)'};
  border: 1px solid ${props => props.primary ? 'rgba(244, 63, 96, 0.3)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
  
  img {
    width: 32px;
    height: 32px;
    margin-bottom: 0.5rem;
  }
  
  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
    
    span {
      color: ${props => props.primary ? '#F43F60' : '#4ECDC4'};
      font-weight: 600;
    }
  }
`;

const RightColumn = styled.div`
  position: sticky;
  top: 2rem;
  height: fit-content;
  
  @media (max-width: 1024px) {
    position: static;
  }
`;

const PriceCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 15px;
  text-align: center;
  
  h2 {
    font-size: 3rem;
    font-weight: 700;
    color: #F43F60;
    margin: 0 0 1.5rem 0;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActionButton = styled.button`
  background: ${props => props.primary ? 'linear-gradient(45deg, #F43F60, #FF6B6B)' : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.primary ? 'transparent' : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  padding: 12px 15px;
  border-radius: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.7);
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  text-align: center;
  gap: 1rem;
  
  h2 {
    color: #F43F60;
    margin: 0;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
  }
`;

const CarDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [images, setImages] = useState([]);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const fetchCarDetails = async () => {
      try {
        const carId = searchParams.get('id');
        if (!carId) {
          setError('No car ID provided');
          setLoading(false);
          return;
        }

        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/inventory/vehicles/${carId}`, {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`
          }
        });
        
        const data = await response.json();
        
        if (data.status === 'success') {
          const vehicle = data.data.vehicle;
          setCar(vehicle);
          
          // Parse imagelist if it exists
          if (vehicle.imagelist) {
            try {
              // Try parsing as JSON first
              let parsedImages = [];
              try {
                parsedImages = JSON.parse(vehicle.imagelist);
              } catch {
                // If JSON parse fails, try splitting CSV
                parsedImages = vehicle.imagelist.split(',').map(url => ({ url: url.trim() }));
              }
              setImages(parsedImages);
            } catch (err) {
              console.error('Error parsing imagelist:', err);
              setImages([]);
            }
          } else {
            setImages([]);
          }
        } else {
          throw new Error(data.message || 'Failed to fetch car details');
        }
      } catch (err) {
        console.error('Error fetching car details:', err);
        setError('Failed to load car details');
      } finally {
        setLoading(false);
      }
    };

    fetchCarDetails();
  }, [searchParams]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
  };

  const handlePrevImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex(prev => 
        prev === 0 ? images.length - 1 : prev - 1
      );
    }
  };

  const handleNextImage = () => {
    if (images.length > 1) {
      setCurrentImageIndex(prev => 
        prev === images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleCallDealer = () => {
    if (car?.dealer_phone_number) {
      window.open(`tel:${car.dealer_phone_number}`, '_self');
    }
  };

  const handleTextDealer = () => {
    if (car?.dealer_phone_number) {
      const message = `Hi, I'm interested in the ${car.year} ${car.make} ${car.model} (Stock ID: ${car.stock_no}). Could you please provide more information?`;
      window.open(`sms:${car.dealer_phone_number}?body=${encodeURIComponent(message)}`, '_self');
    }
  };

  const handleMakeOffer = () => {
    // Navigate to offer form or open modal
    navigate('/?selectedCar=' + car._id);
  };

  const handleScheduleTestDrive = () => {
    // Navigate to test drive scheduling
    navigate('/?selectedCar=' + car._id + '&action=testDrive');
  };

  const handleViewInventory = () => {
    // Navigate to dealer inventory page
    navigate(`/dealer-inventory/${car.dealer_id}`);
  };

  if (loading) {
    return (
      <DetailsContainer>
        <LoadingContainer>Loading car details...</LoadingContainer>
      </DetailsContainer>
    );
  }

  if (error || !car) {
    return (
      <DetailsContainer>
        <ErrorContainer>
          <h2>Error</h2>
          <p>{error || 'Car not found'}</p>
        </ErrorContainer>
      </DetailsContainer>
    );
  }

  return (
    <DetailsContainer>
      <DetailsHeader>
        <HeaderContent>
          <HeaderRow>
            <HeaderTitle>
              <h3>{car.year} {car.make} {car.model}</h3>
              <p>Price ${car.price?.toLocaleString() || car.special_price?.toLocaleString() || 'N/A'}</p>
            </HeaderTitle>
                        <HeaderButtons>
              <ul>
                <li>
                  <HeaderButton onClick={handleCallDealer}>
                    <Phone size={16} />
                    Call
                  </HeaderButton>
                </li>
                <li>
                  <HeaderButton onClick={handleTextDealer}>
                    <MessageCircle size={16} />
                    Text
                  </HeaderButton>
                </li>
                <li>
                  <HeaderButton onClick={handleViewInventory}>
                    <Calendar size={16} />
                    View Inventory
                  </HeaderButton>
                </li>
              </ul>
            </HeaderButtons>
          </HeaderRow>
        </HeaderContent>
      </DetailsHeader>

      <MainContent>
        <VdpArea>
          <DetailsSlider>
            <SliderContainer>
              <SliderImage 
                src={images[currentImageIndex]?.url || '/placeholder-car.jpg'} 
                alt={`${car.make} ${car.model}`}
                onError={(e) => {
                  e.target.src = '/placeholder-car.jpg';
                }}
              />
              {images.length > 1 && (
                <>
                  <SliderNav>
                    <SliderButton onClick={handlePrevImage}>
                      <ChevronLeft size={24} />
                    </SliderButton>
                    <SliderButton onClick={handleNextImage}>
                      <ChevronRight size={24} />
                    </SliderButton>
                  </SliderNav>
                  <ImageCounter>
                    {currentImageIndex + 1} / {images.length}
                  </ImageCounter>
                </>
              )}
            </SliderContainer>
            
            {images.length > 1 && (
              <ThumbnailGallery>
                {images.map((image, index) => (
                  <Thumbnail
                    key={index}
                    src={image.url}
                    alt={`${car.make} ${car.model} - Image ${index + 1}`}
                    className={currentImageIndex === index ? 'active' : ''}
                    onClick={() => handleImageClick(index)}
                    onError={(e) => {
                      e.target.src = '/placeholder-car.jpg';
                    }}
                  />
                ))}
              </ThumbnailGallery>
            )}
          </DetailsSlider>
        </VdpArea>

        <DetailsContent>
          <LeftColumn>
            <CarTitle>
              <p>{car.new_used}</p>
              <h1>{car.year} {car.make} {car.model}</h1>
              <p className="vin">VIN: {car.vin || 'N/A'}</p>
              <p className="description">
                { `The ${car.year} ${car.make} ${car.model} is a ${car.new_used?.toLowerCase()} vehicle with ${car.mileage?.toLocaleString()} miles. This ${car.transmission || 'automatic'} transmission vehicle features ${car.drivetrain || 'standard'} drivetrain and comes in ${car.ext_color || 'various'} exterior color.`}
              </p>
            </CarTitle>

                         <AboutCarsArea>
               <h3>Key Information</h3>
               <AboutCarsGrid>
                 <AboutCar>
                   <CarIcon>
                     <Gauge size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Mileage</h4>
                     <p>{car.mileage?.toLocaleString() || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>
                 
                 <AboutCar>
                   <CarIcon>
                     <Hash size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Stock</h4>
                     <p>{car.stock_no || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>
                 
                 <AboutCar>
                   <CarIcon>
                     <Settings size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Transmission</h4>
                     <p>{car.transmission || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>
                 
                 <AboutCar>
                   <CarIcon>
                     <Car size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Drivetrain</h4>
                     <p>{car.drivetrain || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>
                 
                 <AboutCar>
                   <CarIcon>
                     <Zap size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Engine</h4>
                     <p>{car.engine || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>
                 
                 <AboutCar>
                   <CarIcon>
                     <Palette size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Exterior</h4>
                     <p>{car.ext_color || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>
                 
                 <AboutCar>
                   <CarIcon>
                     <Droplets size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Interior</h4>
                     <p>{car.int_color || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>
                 
                 <AboutCar>
                   <CarIcon>
                     <Gauge size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>MPG City</h4>
                     <p>{car.mpg_city || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>

                 <AboutCar>
                   <CarIcon>
                     <Hash size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Body Style</h4>
                     <p>{car.body_style || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>

                 <AboutCar>
                   <CarIcon>
                     <Settings size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Doors</h4>
                     <p>{car.doors || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>

                 <AboutCar>
                   <CarIcon>
                     <Car size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Trim</h4>
                     <p>{car.trim || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>

                 <AboutCar>
                   <CarIcon>
                     <Zap size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Fuel</h4>
                     <p>{car.fuel || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>

                 <AboutCar>
                   <CarIcon>
                     <Palette size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Cylinders</h4>
                     <p>{car.cylinders || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>

                 <AboutCar>
                   <CarIcon>
                     <Droplets size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>MPG Highway</h4>
                     <p>{car.mpg || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>

                 <AboutCar>
                   <CarIcon>
                     <Gauge size={24} />
                   </CarIcon>
                   <CarInfo>
                     <h4>Certified</h4>
                     <p>{car.certified || 'N/A'}</p>
                   </CarInfo>
                 </AboutCar>
               </AboutCarsGrid>
             </AboutCarsArea>

                         <DetailsCon>
               <h3>Equipment</h3>
               <p>
                 { `This ${car.year} ${car.make} ${car.model} comes equipped with modern features and technology. The vehicle includes ${car.transmission || 'automatic'} transmission, ${car.drivetrain || 'standard'} drivetrain, and is available in ${car.ext_color || 'various'} exterior color with ${car.int_color || 'matching'} interior.`}
               </p>
               <p>
                 The vehicle has been carefully maintained and is ready for immediate delivery. All standard safety features are included, and the vehicle comes with a comprehensive warranty. Contact us today to schedule a test drive or discuss financing options.
               </p>
             </DetailsCon>

             

                           <DetailsCon>
                <h3>Dealer Information</h3>
                <div style={{ 
                  background: 'rgba(255, 255, 255, 0.05)', 
                  border: '1px solid rgba(255, 255, 255, 0.1)', 
                  borderRadius: '12px', 
                  padding: '20px',
                  marginBottom: '1rem'
                }}>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                    gap: '1.5rem' 
                  }}>
                    <div>
                      <h4 style={{ 
                        fontSize: '0.9rem', 
                        fontWeight: '500', 
                        color: 'rgba(255, 255, 255, 0.7)', 
                        margin: '0 0 0.5rem 0', 
                        textTransform: 'uppercase' 
                      }}>Dealer Name</h4>
                      <p style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#ffffff', 
                        margin: '0' 
                      }}>{car.first_name} {car.last_name}</p>
                    </div>
                    
                    <div>
                      <h4 style={{ 
                        fontSize: '0.9rem', 
                        fontWeight: '500', 
                        color: 'rgba(255, 255, 255, 0.7)', 
                        margin: '0 0 0.5rem 0', 
                        textTransform: 'uppercase' 
                      }}>Phone</h4>
                      <p style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#ffffff', 
                        margin: '0' 
                      }}>{car.dealer_phone_number || 'N/A'}</p>
                    </div>
                    
                    <div>
                      <h4 style={{ 
                        fontSize: '0.9rem', 
                        fontWeight: '500', 
                        color: 'rgba(255, 255, 255, 0.7)', 
                        margin: '0 0 0.5rem 0', 
                        textTransform: 'uppercase' 
                      }}>Email</h4>
                      <p style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#ffffff', 
                        margin: '0' 
                      }}>{car.email || 'N/A'}</p>
                    </div>
                    
                    <div>
                      <h4 style={{ 
                        fontSize: '0.9rem', 
                        fontWeight: '500', 
                        color: 'rgba(255, 255, 255, 0.7)', 
                        margin: '0 0 0.5rem 0', 
                        textTransform: 'uppercase' 
                      }}>City</h4>
                      <p style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#ffffff', 
                        margin: '0' 
                      }}>{car.city || 'N/A'}</p>
                    </div>
                    
                    <div>
                      <h4 style={{ 
                        fontSize: '0.9rem', 
                        fontWeight: '500', 
                        color: 'rgba(255, 255, 255, 0.7)', 
                        margin: '0 0 0.5rem 0', 
                        textTransform: 'uppercase' 
                      }}>Address</h4>
                      <p style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#ffffff', 
                        margin: '0' 
                      }}>{car.address || 'N/A'}</p>
                    </div>
                    
                    <div>
                      <h4 style={{ 
                        fontSize: '0.9rem', 
                        fontWeight: '500', 
                        color: 'rgba(255, 255, 255, 0.7)', 
                        margin: '0 0 0.5rem 0', 
                        textTransform: 'uppercase' 
                      }}>Dealer ID</h4>
                      <p style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: '#ffffff', 
                        margin: '0' 
                      }}>{car.dealer_id || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              </DetailsCon>

             

             

             {car.description && (
               <DetailsCon>
                 <h3>Description</h3>
                 <p>{car.description}</p>
               </DetailsCon>
             )}

             {car.options && (
               <DetailsCon>
                 <h3>Options</h3>
                 <p>{car.options}</p>
               </DetailsCon>
             )}

                           <DetailsCon>
                <h3>Vehicle Information</h3>
                <div style={{ 
                  background: 'rgba(255, 255, 255, 0.05)', 
                  border: '1px solid rgba(255, 255, 255, 0.1)', 
                  borderRadius: '12px', 
                  padding: '20px',
                  marginBottom: '1rem'
                }}>
                  <div>
                    <h4 style={{ 
                      fontSize: '0.9rem', 
                      fontWeight: '500', 
                      color: 'rgba(255, 255, 255, 0.7)', 
                      margin: '0 0 0.5rem 0', 
                      textTransform: 'uppercase' 
                    }}>Vehicle ID</h4>
                    <p style={{ 
                      fontSize: '16px', 
                      fontWeight: '600', 
                      color: '#ffffff', 
                      margin: '0' 
                    }}>{car.id || 'N/A'}</p>
                  </div>
                </div>
              </DetailsCon>

           </LeftColumn>

          <RightColumn>
            <PriceCard>
              <h2>${car.price?.toLocaleString() || car.special_price?.toLocaleString() || 'N/A'}</h2>
              <ActionButtons>
                <ActionButton onClick={handleCallDealer}>
                  Call
                </ActionButton>
                <ActionButton onClick={handleTextDealer}>
                  Text
                </ActionButton>
                <ActionButton onClick={handleViewInventory}>
                  View Inventory
                </ActionButton>
              </ActionButtons>
            </PriceCard>
          </RightColumn>
        </DetailsContent>
      </MainContent>
    </DetailsContainer>
  );
};

export default CarDetails; 